import React, { Suspense } from "react";
import RouteFallback from "./components/RouteFallback";

 // EAGER — core condo path + the app launcher.
 // `/` redirects to `/tileapps`, so the launcher tiles are the primary landing
 // surface; the condoManagement dashboard + its auth components are the main
 // vertical. Keeping these statically imported avoids a Suspense flash on first
 // paint of the primary journey.
 import AppPage from "./pages/tiles/TileApp";
 import TileAppPage from "./pages/okta/TileApps";
 import CompanyHome from "./pages/landing/CompanyHome";
 import ProductsLanding from "./pages/landing/ProductsLanding";
 import SignupWizard from "./pages/landing/SignupWizard";
 import SignIn from "./pages/landing/SignIn";
 import MyApps from "./pages/landing/MyApps";
 import TeamRequests from "./pages/landing/TeamRequests";
 import AboutUsPage from "./pages/landing/AboutUsPage";
 import Payments from "./pages/landing/Payments";
 import MainDashboard from "./pages/condoManagement/MainDashboard";
 import LoginCondo from "./pages/condoManagement/components/auth/Login";
 import SetPassword from "./pages/condoManagement/components/auth/SetPassword";
 import RequestStatusCertificate from "./pages/condoManagement/components/auth/RequestStatusCertificate";
 import NotFoundPage from "./pages/NotFoundPage";
 import CondoProtectedRoute from "./pages/condoManagement/components/auth/ProtectedRoute";
 import { UnifiedAuthProvider } from './context/UnifiedAuthContext';
 import { ConfigProvider, App as AntdApp } from 'antd';
 import { antdTheme, atriumAntdTheme } from './theme/theme';
 import { useAtriumRedesign } from './pages/condoManagement/hooks/useAtriumRedesign';
 import { StaticApiBridge } from './pages/condoManagement/utils/staticApi';
 import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
 import { defaultLandingPath, isCustomerMode } from './config/customerMode';

 // Sprint 98 — route-level code-splitting.
 // Each of these is a SEPARATE vertical (medTech, reportGenerator, fin_auditor,
 // tenant-management, triage, scheduler, payment). A condo user never visits
 // them, yet they were eagerly bundled into the single SPA chunk. React.lazy +
 // <Suspense> (boundary in the render tree below) defers each vertical's code
 // until its route is actually navigated to. Every target module is verified to
 // have a DEFAULT export, so no .then() unwrap is needed.
 // Sprint 98: TRIAGEAPP / REPORTAPP were dead imports (never routed) — removed.
 //import EDUCATIONAPP from "./pages/edu_games_sys/GameApp";
 const SHEDULINGAPP = React.lazy(() => import("./pages/maintance_scheduler/SchedelingApp"));
 const FINAUDITOR = React.lazy(() => import("./pages/fin_auditor/FinAuditor"));
 const PAYMENTFLOW = React.lazy(() => import("./pages/payment/PaymentFlow"));
 const MainMedPage = React.lazy(() => import("./pages/medTech/MainPage"));
 //import DPApp from "./pages/disaster_recovery/DPApp";
 const TenantApp = React.lazy(() => import("./pages/tenant-management/TenantApp"));
 const MedTechLogin = React.lazy(() => import("./pages/medTech/pages/Login"));
 //import AuthContext from "./context/UnifiedAuthContext";

// https://firebase.google.com/docs/web/setup#available-libraries

const Return = () => {
  return null;
}


export default function App() {

  // Sprint 89B (M3) — Atrium modal-theme fix via the ConfigProvider/token path.
  //
  // ROOT CAUSE the deep E2E found: modal primary CTAs rendered #1890ff (AntD's
  // DEFAULT blue) — NOT #6366f1 (legacy global) and NOT #2563eb (Atrium). That
  // specific colour proves the measured portal surfaces inherited NO React
  // ConfigProvider context at all. The only AntD surfaces that escape *every*
  // React provider are the STATIC imperative APIs (Modal.confirm / message /
  // notification) — and this app never mounted AntD's <App> holder, so those
  // methods fell back to the global default token. The Sprint 88B CSS hack lost
  // because AntD writes colorPrimary as a higher-specificity CSS-in-JS rule.
  //
  // FIX (Option A): make the GLOBAL ConfigProvider flag-aware. It is the
  // outermost AntD provider and therefore the guaranteed ancestor of EVERY
  // declarative <Modal> portal (React context crosses portals). Wrapping the
  // tree in AntD's <App> holder additionally routes the static message/
  // notification surfaces through context. Flag OFF ⇒ antdTheme (#6366f1)
  // unchanged ⇒ other monorepo apps untouched + fully reversible.
  const atriumOn = useAtriumRedesign();
  const activeTheme = atriumOn ? atriumAntdTheme : antdTheme;

  // Sprint 128.1 (ADR-121) — packaging: in a CUSTOMER build the sibling DEMO
  // verticals (scheduler, fin-auditor, payment-flow, med-tec, and the duplicate
  // tenant-management surface) must be UNREACHABLE — not merely hidden. Sprint 98
  // already code-split each vertical into its own lazy chunk (so they were never
  // in the main chunk), and Sprint 115 hid the launcher tiles; but the routes
  // stayed registered, so a customer could still reach a demo app by typing the
  // URL, and its chunk would download on demand. Gating the <Route> registration
  // itself means: (a) in customer mode those paths fall through to the catch-all
  // → redirect to CondoPro, and (b) the lazy chunks are never fetched in a
  // customer session. DEMO mode (default, dev) is UNCHANGED — every demo route
  // is registered exactly as before. `isCustomerMode()` reads the build-time
  // REACT_APP_CUSTOMER_MODE env (CRA inlines it), so this is a compile-time
  // constant per build. See config/customerMode.ts.
  const customerMode = isCustomerMode();

  // Theme the STATIC imperative surfaces too. AntD's `message.*` /
  // `notification.*` / `Modal.confirm` render into a detached root with NO React
  // ancestry, so the ConfigProvider context below cannot reach them. The
  // supported way to theme them is the static `ConfigProvider.config({ theme })`
  // registry. Re-registered whenever the flag flips. (Reversible: flag OFF
  // re-registers antdTheme.)
  React.useEffect(() => {
    ConfigProvider.config({ theme: activeTheme as any });
  }, [activeTheme]);

  return (
    <>
 {/* AntD ConfigProvider applies the central design tokens (see theme/theme.ts
     and THEMING.md). All AntD components nested below pick up the palette,
     radii, and component-level overrides automatically. When the Atrium flag is
     on we swap in atriumAntdTheme (#2563EB) here at the app root so every
     portaled modal — which inherits the NEAREST ConfigProvider via React
     context even when rendered into document.body — gets Atrium blue. */}
 <ConfigProvider theme={activeTheme}>
 {/* Sprint 94 (finding 94.3) — AntD <App> context holder.
     AntD's STATIC imperative APIs (`Modal.confirm`, `message.*`,
     `notification.*` imported directly from 'antd') render into a detached
     root with NO React ancestry, so under React 19 + AntD 6 they cannot
     consume this ConfigProvider's theme and log:
       "static function can not consume context like dynamic theme".
     Mounting AntD's <App> here — INSIDE <ConfigProvider> (so it inherits the
     active theme) and OUTSIDE the routes — provides the context-aware
     message/notification/modal instances. `component={false}` keeps it a pure
     context holder with NO extra DOM wrapper, so layout/styling is unchanged.

     <StaticApiBridge/> captures those instances into a module-level holder so
     non-component code can call `staticApi.message.success(...)` etc. (see
     ./pages/condoManagement/utils/staticApi.ts).

     DEFERRED MIGRATION: the ~91 existing call sites across the app still use
     the raw static `Modal`/`message`/`notification` imports from 'antd'. They
     are intentionally left as-is here; migrating them to `staticApi.*` is a
     tracked follow-up. This change is infrastructure only. */}
 <AntdApp component={false}>
 <StaticApiBridge />
 <UnifiedAuthProvider defaultApp="general">
  <Router>
    {/* Sprint 98 — single Suspense boundary for all lazily code-split verticals.
        Placed INSIDE <Router> (router context available to fallback + lazy
        routes) and OUTSIDE <Routes> so one boundary covers every lazy route.
        Eager routes (launcher, condo dashboard/login) render without ever
        suspending; only the lazy verticals show <RouteFallback/> while their
        chunk downloads. */}
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/return" element={<Return />} />

        {/* Sprint 128.1 (ADR-121) — the DEMO verticals + their launchers are
            registered ONLY in demo mode. In a customer build (customerMode)
            these paths are NOT routes at all, so they fall through to the
            catch-all below and redirect into CondoPro; their lazy chunks are
            never fetched. Dev/demo (default) is unchanged. The duplicate
            tenant-management surface (`/tenant-app/`, `/apps` launcher) is thus
            unreachable in customer mode. */}
        {!customerMode && (
          <>
            <Route path="/apps" element={<AppPage />} />
            <Route path="/sheduling-app" element={<SHEDULINGAPP />} />
            <Route path="/fin-auditor-app" element={<FINAUDITOR />} />
            <Route path="/payment-flow-app" element={<PAYMENTFLOW />} />
            <Route path="/tenant-app/" element={<TenantApp />} />
            <Route path="/med-tec-app/" element={<MainMedPage />} />
            <Route path="/login-med-app/" element={<MedTechLogin />} />
          </>
        )}

        {/* The launcher grid stays registered in both modes but self-filters to
            only the CondoPro tile under customerMode (see okta/TileApps.tsx);
            it's normally bypassed anyway since `/` → /condo-app/ in customer
            mode. */}
        <Route path="/tileapps" element={<TileAppPage />} />

        {/* Pulm marketing + preview-access flow. Registered in both modes;
            /products is the demo-mode home (see the "/" redirect below).
            signup wizard → team approval (/team/requests) → sign in → my-apps. */}
        <Route path="/products" element={<ProductsLanding />} />
        <Route path="/signup" element={<SignupWizard />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/my-apps" element={<MyApps />} />
        <Route path="/team/requests" element={<TeamRequests />} />
        <Route path="/about" element={<AboutUsPage />} />
        <Route path="/pay" element={<Payments />} />

        <Route
          path="/condo-app/"
          element={
            <CondoProtectedRoute>
              <MainDashboard />
            </CondoProtectedRoute>
          }
        />
        <Route
          path="/condo-app/*"
          element={
            <CondoProtectedRoute>
              <MainDashboard />
            </CondoProtectedRoute>
          }
        />
        <Route path="/login-condo-app/" element={<LoginCondo />}>
        </Route>
        {/* Sprint 124 (ADR-117) — public owner activation / set-password page,
            reachable WITHOUT auth via the emailed/dev-logged link
            /set-password?token=… . */}
        <Route path="/set-password" element={<SetPassword />} />
        {/* Sprint 133.1 — public status-certificate request page, reachable
            WITHOUT auth via the shared link /request-status-certificate?condo=<uuid> .
            An external requestor (buyer's solicitor) submits a request; the
            backend answers uniformly and never leaks tenant data. */}
        <Route path="/request-status-certificate" element={<RequestStatusCertificate />} />
        <Route path="/condo-app/login" element={<Navigate to="/login-condo-app/" replace />} />

        {/* Sprint 27 — finding S1. Bare "/" used to render a blank page
            (no route matched, no error). Send anonymous visitors to the
            app launcher; CondoProtectedRoute already bounces unauthed
            users to /login-condo-app/ on the condo path so we don't need
            a session-aware redirect here. The catch-all path="*" covers
            mistyped URLs the same way.

            Sprint 115 (ADR-108) — customer-mode packaging. In DEMO mode
            (default) this stays `/tileapps` so dev behavior is identical.
            In CUSTOMER mode (REACT_APP_CUSTOMER_MODE=true) it resolves to
            `/condo-app/` so a customer lands directly in CondoPro and never
            sees the sibling demo launcher. See config/customerMode.ts. */}
        <Route path="/" element={customerMode ? <Navigate to={defaultLandingPath()} replace /> : <CompanyHome />} />

        {/* Sprint 147 (ADR-142, R14-20). The catch-all used to redirect a
            mistyped URL to the launcher with no 404 — the address bar changed
            and nothing said the URL was wrong. Unknown IN-APP slugs have been
            honest since R11-8 (ADR-128); the outer router now matches.

            CUSTOMER mode keeps the redirect: Sprint 128.1 (ADR-121) gates the
            demo-vertical routes off and relies on this catch-all to bounce a
            customer who types one back into CondoPro. */}
        <Route
          path="*"
          element={customerMode
            ? <Navigate to={defaultLandingPath()} replace />
            : <NotFoundPage />}
        />

        {/* <Route path="/des-rec-app/" element={<DPApp />}>
        </Route> */}
    
          {/* <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardMainPage />
          </ProtectedRoute>
        } /> */}
          
 
      </Routes>
    </Suspense>
    </Router>
  </UnifiedAuthProvider>
 </AntdApp>
 </ConfigProvider>

  </>

   
  );
}
